import { useEffect } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useFragment } from '@apollo/client';
import queryString from 'query-string';

import { TagsEnum } from '@models';
import { productDetails } from '@legacyUrls';
import {
  safeFbq,
  sendOpenMyOwnProductEvent,
  sendProductPageViewEvent,
  sendProductViewItemEvent,
  sendRTBHouseProductEvent,
} from '@utils/analytics';
import { useProductScore } from '@hook/useProductScore';
import {
  getAnaliticAppliedFiltersFromStorage,
  getProductPageReferrerFromStorage,
  getProductReferrerPathnameFromStorage,
} from '@utils/common';
import { useProductIncrViews } from '@hook/useProductIncrViews';
import {
  ClerkActionType,
  ClerkCategoryType,
  ClerkViewItemEvent,
  useClerk,
} from '@hook/useClerk';

import { useProductDetailsAnalyticsSocialPostsQuery } from './gql/graphql.v5.generated';
import {
  ProductDetailsAnalyticsFragment,
  ProductDetailsAnalyticsFragmentDoc,
  ProductDetailsCatalogAnalyticsFragment,
  ProductDetailsCatalogAnalyticsFragmentDoc,
} from './gql/graphql.v3.generated';

type Product = {
  __typename?: 'ProductDetails',
  id: number,
}

type Catalog = {
  __typename?: 'RootCatalog',
  id: string,
}

interface Props {
  product?: Product | null,
  catalog?: Catalog | null,
  withIncrViews?: boolean,
}

export const ProductDetailsAnalyticsContainer: React.FC<Props> = ({
  product,
  catalog,
  withIncrViews,
}) => {
  const location = useLocation();
  const match = useRouteMatch<{ productId: string }>(productDetails);

  const { sendClerkEvent } = useClerk();

  const searchParams = queryString.parse(location.search);

  const isFromAdvert = Boolean(searchParams['from-adv']);

  const { data: productData, complete: productDataComplete } = useFragment<ProductDetailsAnalyticsFragment>({
    fragment: ProductDetailsAnalyticsFragmentDoc,
    from: product || null,
  });

  const { data: catalogData, complete: catalogDataComplete } = useFragment<ProductDetailsCatalogAnalyticsFragment>({
    fragment: ProductDetailsCatalogAnalyticsFragmentDoc,
    from: catalog || null,
  });

  const {
    data: productSocialPostsData,
  } = useProductDetailsAnalyticsSocialPostsQuery({
    variables: {
      productId: match?.params.productId as string,
    },
    context: {
      batch: true,
      v5: true,
    },
    skip: !match?.params.productId,
  });

  useProductIncrViews({
    productId: Number(match?.params.productId),
    isFromAdvert,
    isIncrViews: withIncrViews && productDataComplete && !productData?.isOwner,
  });

  const { progress } = useProductScore({
    waitScore: true,
    photos: productDataComplete ? productData?.photos : undefined,
    titleUk: productDataComplete ? productData?.name : undefined,
    titleRu: productDataComplete ? productData?.name : undefined,
    descriptionUk: productDataComplete ? productData?.description : undefined,
    descriptionRu: productDataComplete ? productData?.description : undefined,
    catalogSlug: productDataComplete ? productData?.catalog?.slug : undefined,
    price: productDataComplete ? productData?.price : undefined,
    characteristics: productDataComplete ? productData?.characteristics : undefined,
    sizes: productDataComplete ? productData?.sizes : undefined,
    colors: productDataComplete ? productData?.colors : undefined,
    condition: productDataComplete ? productData?.condition : undefined,
    keywords: productDataComplete ? productData?.keyWords : undefined,
  });

  useEffect(() => {
    if (productDataComplete && catalogDataComplete && productSocialPostsData?.postsByProduct && progress) {
      const product = productData;
      const catalog = catalogData;
      const productSocialPosts = productSocialPostsData?.postsByProduct;
      const storageReferrer = getProductPageReferrerFromStorage(product.id);
      const productReferrerPathname = getProductReferrerPathnameFromStorage(product.id);
      const analiticAppliedFilters = productReferrerPathname ? getAnaliticAppliedFiltersFromStorage(productReferrerPathname) : null;

      let ownerType: 'super-seller' | 'new-seller' | 'regular-seller' = 'regular-seller';

      if (product.seller.isTopSeller) {
        ownerType = 'super-seller';
      } else if (product.seller.isNewSeller) {
        ownerType = 'new-seller';
      }

      sendClerkEvent<ClerkViewItemEvent>({
        category: ClerkCategoryType.viewItem,
        action: ClerkActionType.show,
        payload: {
          productId: product.id,
          sellerOrderRate: product.seller.orderRate || '0',
          product_scale: String(progress),
          ...(typeof product.inPriceRange === 'boolean' && {
            inPriceRange: product.inPriceRange,
          }),
          ...(storageReferrer && {
            referrer: storageReferrer,
          }),
        },
      });

      sendProductViewItemEvent({
        id: String(product.id),
        reviews: !!product.ratingAmount,
        price: product.price || 0,
        name: product.name,
        brandName: product.brand?.name || '',
        categorySlug: product.catalog?.slug || 'clothes',
        freeShipping: product.tags.includes(TagsEnum.FreeShipping),
        ownerType,
        socialPosts: !!productSocialPosts.edges.length,
        isPrivateSeller: product.seller.isPrivateSeller,
        scale: String(progress),
        ...(!!analiticAppliedFilters && {
          filters: analiticAppliedFilters,
        }),
        ...(storageReferrer && {
          referrer: storageReferrer,
        }),
      });

      sendProductPageViewEvent({
        id: String(product.id),
        price: product.price || 0,
      });

      safeFbq('track', 'ViewContent', {
        content_name: product.name,
        content_category: catalog.breadcrumbs.join(' > '),
        content_ids: [String(product.id)],
        content_type: 'product',
        value: product.price || 0,
        currency: 'UAH',
      });

      if (product.isOwner) {
        sendOpenMyOwnProductEvent();
      } else {
        sendRTBHouseProductEvent(String(product.id));
      }
    }
  }, [
    catalogData,
    catalogDataComplete,
    productData,
    productDataComplete,
    location.search,
    productSocialPostsData?.postsByProduct,
    progress,
    sendClerkEvent,
  ]);

  return null;
};
